import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FLOATING_ELEMENT_PLACEMENT } from '../../../../constants/common'
import { useCopyToClipboard } from '../../../../hooks'
import { isClipboardWriteSupported } from '../../../../utils/clipboard'
import { stopPropagation } from '../../../../utils/events'
import Button, { BUTTON_HEIGHT_24 } from '../../../Button'
import { ICON_HEIGHT_16 } from '../../../Icon'
import { GRETA_COPY_FEEDBACK_TIMEOUT_MS } from '../../GretaAIChat.constants'
import { QA_NAME_GRETA_AI_CHAT_MESSAGE_COPY } from '../GretaAIChatMessageActions.constants'
import {
  copyGretaAIChatReplyRich,
  getGretaAIChatClipboardHtml,
  isGretaAIChatRichClipboardSupported,
} from '../GretaAIChatMessageActions.utils'

import CopyIcon from '../../../../icons/copy.svg'
import DoneIcon from '../../../../icons/done.svg'

function GretaAIChatCopyButton({ className, contentRef, text }) {
  const { t } = useTranslation()

  const canCopy = isClipboardWriteSupported()

  const [copied, setCopied] = useState(false)
  const timerRef = useRef(null)

  function handleCopySuccess() {
    setCopied(true)

    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      setCopied(false)
    }, GRETA_COPY_FEEDBACK_TIMEOUT_MS)
  }

  const [, copy] = useCopyToClipboard({ onSuccess: handleCopySuccess })

  useEffect(() => () => clearTimeout(timerRef.current), [])

  function copyReplyFallback(node) {
    const content = node.innerText.trim()

    if (content) {
      copy(content, { html: getGretaAIChatClipboardHtml(node) })
    }
  }

  function handleCopy(event) {
    stopPropagation(event)

    const node = contentRef?.current

    if (node) {
      const markdown = text?.trim()

      // Synchronous branch: an await here would spend the gesture the write needs.
      if (markdown && isGretaAIChatRichClipboardSupported()) {
        copyGretaAIChatReplyRich({ markdown, node }).then(copiedRich => {
          if (copiedRich) {
            handleCopySuccess()
          } else {
            copyReplyFallback(node)
          }
        })

        return
      }

      copyReplyFallback(node)

      return
    }

    if (text) {
      copy(text)
    }
  }

  return canCopy ? (
    <Button.Icon
      isGrey
      Icon={copied ? DoneIcon : CopyIcon}
      beforeIconProps={{ height: ICON_HEIGHT_16 }}
      className={className}
      height={BUTTON_HEIGHT_24}
      qaName={QA_NAME_GRETA_AI_CHAT_MESSAGE_COPY}
      tooltip={copied ? t('Greta.CopiedKey') : t('Greta.CopyMessageKey')}
      tooltipPlacement={FLOATING_ELEMENT_PLACEMENT.top}
      onClick={handleCopy}
    />
  ) : null
}

export default GretaAIChatCopyButton
